import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'

/** Ab so vielen Pixeln Bewegung ist ein Druck auf den Griff ein Zug. */
export const MAGNET_DRAG_THRESHOLD = 4

/**
 * Wie lange die Pille ins Ziel gleitet — die `transition` der Pille muss dazu
 * passen. Ein fester Timer statt `transitionend`: Das Ereignis bleibt aus, wenn
 * der Tab unsichtbar wird oder der Versatz schon stimmt, und die Pille hinge
 * dann ewig in `settling`.
 */
export const MAGNET_SETTLE_MS = 200

export interface MagnetDragGeometry<K extends string> {
  /** Die Pille selbst, in Client-Koordinaten. */
  pill: DOMRect
  /**
   * Wo die Pille an jedem Ziel stünde. Das Ziel `current` ist die Stelle, an
   * der sie gerade steht; der Abstand zu einem anderen Ziel ist der Weg, den
   * sie beim Einrasten gleitet.
   */
  targets: Record<K, DOMRect>
}

export type MagnetDragPhase = 'idle' | 'dragging' | 'settling'

interface UseMagnetDragOptions<K extends string> {
  /** Das Ziel, an dem die Pille gerade steht. */
  current: K
  /** Die Pille — bekommt `--drag-x`/`--drag-y` imperativ, ohne Rerender je Frame. */
  pillRef: React.RefObject<HTMLElement | null>
  /** Misst beim Zugbeginn. `null` = kein Zug. */
  measure: () => MagnetDragGeometry<K> | null
  /** Die Pille ist an einem anderen Ziel eingerastet. */
  onChange: (next: K) => void
}

export interface MagnetDragHandle<K extends string> {
  gripPointerDown: (event: React.PointerEvent) => void
  phase: MagnetDragPhase
  /** Welches Ziel sich gerade meldet — nur während des Zugs. */
  nearest: K | null
}

const centerX = (r: DOMRect) => r.left + r.width / 2

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
  } catch {
    return false
  }
}

/**
 * Eine Pille am **Griff** zu einem von mehreren Zielen ziehen — sie rastet
 * dort ein wie an einem Magneten. Gebraucht von der Werkzeug-Pille der
 * Pinnwand (zwei Seiten) und der Listen-Werkzeug-Pille (drei Stellen).
 *
 * **Das nächste Ziel gewinnt**, gemessen waagerecht von der Pillenmitte zur
 * Zielmitte. Die Höhe zählt nicht: Alle Ziele liegen in einer Reihe, und wer
 * die Pille schräg trägt, meint trotzdem eine Stelle. Bei Gleichstand gewinnt
 * das spätere Ziel in `targets`.
 *
 * **Loslassen gleitet** in das Ziel, das sich meldet: Versatz auf den Abstand
 * der beiden Ziele, `settling`, und erst nach der Bewegung `onChange`. Steht
 * die Pille danach am neuen Ziel, liegt sie bei Versatz 0 genau dort, wo sie
 * gelandet ist — kein Sprung. Escape ist ein Loslassen, das immer zurückführt.
 *
 * Der Versatz läuft als CSS-Variable (`--drag-x`/`--drag-y`), rAF-gedrosselt
 * und ohne `setState` — Zustand ändert sich nur mit Phase oder nächstem Ziel.
 *
 * @example
 * const drag = useMagnetDrag({ current: position, pillRef, measure, onChange: setListToolbarPosition })
 * <ListToolbar onGripPointerDown={drag.gripPointerDown} gripPressed={drag.phase === 'dragging'} />
 */
export function useMagnetDrag<K extends string>({
  current, pillRef, measure, onChange,
}: UseMagnetDragOptions<K>): MagnetDragHandle<K> {
  const [phase, setPhase] = useState<MagnetDragPhase>('idle')
  const [nearest, setNearest] = useState<K | null>(null)

  const startRef = useRef<{ x: number; y: number } | null>(null)
  const geometryRef = useRef<MagnetDragGeometry<K> | null>(null)
  const nearestRef = useRef<K | null>(null)
  const latestRef = useRef<{ x: number; y: number } | null>(null)
  const frameRef = useRef(0)
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  // Über Refs gespiegelt, damit die Fenster-Lauscher nicht neu gebunden werden.
  const currentRef = useRef(current)
  currentRef.current = current
  const measureRef = useRef(measure)
  measureRef.current = measure
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const setOffset = useCallback((x: number | null, y: number | null) => {
    const pill = pillRef.current
    if (!pill) return
    if (x === null || y === null) {
      pill.style.removeProperty('--drag-x')
      pill.style.removeProperty('--drag-y')
      return
    }
    pill.style.setProperty('--drag-x', `${x}px`)
    pill.style.setProperty('--drag-y', `${y}px`)
  }, [pillRef])

  const stopListening = useCallback(() => {
    cleanupRef.current?.()
    cleanupRef.current = null
    cancelAnimationFrame(frameRef.current)
    frameRef.current = 0
    startRef.current = null
    latestRef.current = null
  }, [])

  const settle = useCallback((into: K) => {
    const geometry = geometryRef.current
    stopListening()
    // Der Zug hatte noch nicht begonnen (Klick auf den Griff).
    if (!geometry) return

    const from = geometry.targets[currentRef.current]
    const to = geometry.targets[into]
    setOffset(to.left - from.left, to.top - from.top)
    setPhase('settling')

    const finish = () => {
      settleTimerRef.current = null
      geometryRef.current = null
      nearestRef.current = null
      setOffset(null, null)
      setNearest(null)
      setPhase('idle')
      if (into !== currentRef.current) onChangeRef.current(into)
    }
    settleTimerRef.current = setTimeout(finish, prefersReducedMotion() ? 0 : MAGNET_SETTLE_MS)
  }, [setOffset, stopListening])

  const gripPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0) return
    if (settleTimerRef.current) return
    event.preventDefault()

    startRef.current = { x: event.clientX, y: event.clientY }
    const target = event.currentTarget as HTMLElement | undefined
    try {
      target?.setPointerCapture?.(event.pointerId)
    } catch {
      // Kein Capture (Test-DOM) — die Fenster-Lauscher tragen es auch so.
    }

    const handleMove = (e: PointerEvent) => {
      const start = startRef.current
      if (!start) return
      latestRef.current = { x: e.clientX, y: e.clientY }

      if (!geometryRef.current) {
        const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y)
        if (moved < MAGNET_DRAG_THRESHOLD) return
        const geometry = measureRef.current()
        if (!geometry) return
        geometryRef.current = geometry
        setPhase('dragging')
      }

      if (frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0
        const geometry = geometryRef.current
        const latest = latestRef.current
        const origin = startRef.current
        if (!geometry || !latest || !origin) return

        const dx = latest.x - origin.x
        const dy = latest.y - origin.y
        setOffset(dx, dy)

        const x = centerX(geometry.pill) + dx
        let next: K | null = null
        let best = Infinity
        for (const key of Object.keys(geometry.targets) as K[]) {
          const distance = Math.abs(centerX(geometry.targets[key]) - x)
          if (distance <= best) {
            best = distance
            next = key
          }
        }
        if (next !== null && next !== nearestRef.current) {
          nearestRef.current = next
          setNearest(next)
        }
      })
    }
    const handleUp = () => settle(nearestRef.current ?? currentRef.current)
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (!geometryRef.current) return
      // Sonst höbe `useCanvasEscape` (oder die Liste) zugleich die Auswahl auf.
      e.stopPropagation()
      settle(currentRef.current)
    }

    window.addEventListener('pointermove', handleMove, { passive: true })
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
    window.addEventListener('keydown', handleKey, { capture: true })
    cleanupRef.current = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      window.removeEventListener('keydown', handleKey, { capture: true })
    }
  }, [setOffset, settle])

  useEffect(() => () => {
    stopListening()
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current)
  }, [stopListening])

  return { gripPointerDown, phase, nearest }
}
